import type { Context } from '@/@types/ssr';

import { createGlobalState } from '@vueuse/core';
import { getCurrentInstance } from 'vue';
import { getVm } from '@/helpers/vm-helper';

function useContext() {
	const { proxy: instance } = getCurrentInstance() ?? { proxy: getVm() };
	return instance.$context as Context;
}

/**
 * createSafeGlobalState
 * ---------------------
 * • Client  – identical to createGlobalState (one singleton per tab).
 * • Server  – one singleton per *request* (stored on nuxtApp),
 *             so no cross-request state pollution.
 */
export function createSafeGlobalState<T>(factory: () => T) {
	// One key per invocation of this helper
	const key = Symbol('safeGlobalState');

	// Client singleton – created once and kept forever
	const getClientState = createGlobalState(factory);

	return () => {
		if (process.client) return getClientState();

		// 🌐 server – scoped to the current Nuxt app (= current request)
		const context = useContext();

		// Lazily initialise per request
		if (!context._safeGlobalStates) context._safeGlobalStates = new Map<symbol, unknown>();

		if (!context._safeGlobalStates.has(key)) context._safeGlobalStates.set(key, factory());

		return context._safeGlobalStates.get(key) as T;
	};
}
